/* eslint-disable spire/export-styles */
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import { COLORS as SURYA_COLORS, TYPOGRAPHY as SURYA_TYPOGRAPHY } from "@tSurya/theme/design.surya";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
});

const enum fields {
    horizontalTextAlignment = "horizontalTextAlignment",
    marginAllSide = "marginAllSide",
    textTransform = "textTransform",
    mobileMarginAllSide = "mobileMarginAllSide",
    mobileFontSize = "mobileFontSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.horizontalTextAlignment]: "left" | "center" | "right";
        [fields.marginAllSide]: string;
        [fields.textTransform]: "capitalize" | "uppercase" | "lowercase" |  "none";
        [fields.mobileMarginAllSide]: string;
        [fields.mobileFontSize]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const PageTitle: React.FunctionComponent<Props> = ({ pageTitle, fields }: Props) => {
    let isMyAccountPage = false;
    if (typeof window !== "undefined") {
        isMyAccountPage = /^\/myaccount(\/|$)/i.test(window?.location?.pathname || "");
    }

    const typographyConfig = isMyAccountPage ? SURYA_TYPOGRAPHY : TYPOGRAPHY;
    // On MGBW, My Account page titles render in the brand's #333333 text color.
    // Surya uses its #323132 accent for these titles, so re-pin MGBW My Account
    // titles to the Surya accent. Scoped to MGBW only - Surya and Global Views
    // keep their own resolved text.main color.
    const titleColor =
        isMyAccountPage && isThemeActive(THEME_IDS.MGBW) ? SURYA_COLORS.text.accent : getColor("text.main");
    const titleStyle = {
        css: css`
            text-align: ${fields.horizontalTextAlignment};
            text-transform: ${ fields.textTransform};
            font-size: ${typographyConfig.pageTitle.size};
            line-height: ${typographyConfig.pageTitle.lineHeight};
            margin: ${fields.marginAllSide};
            font-weight: ${typographyConfig.pageTitle.weight};
            font-family: ${typographyConfig.pageTitle.fontFamily};
            font-style: ${typographyConfig.pageTitle.fontStyle};
            color: ${titleColor} !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: block;
                        margin-bottom: 15px;
                        ${fields.mobileMarginAllSide ? `margin: ${fields.mobileMarginAllSide};` : ""}
                        ${fields.mobileFontSize ? `font-size: ${fields.mobileFontSize};` : ""}
                    `,
                    css`
                        display: block;
                        margin-bottom: 15px;
                        ${fields.mobileMarginAllSide ? `margin: ${fields.mobileMarginAllSide};` : ""}
                        ${fields.mobileFontSize ? `font-size: ${fields.mobileFontSize};` : ""}
                    `,
                    css`
                        display: block;
                        margin-bottom: 15px;
                        ${fields.mobileMarginAllSide ? `margin: ${fields.mobileMarginAllSide};` : ""}
                        ${fields.mobileFontSize ? `font-size: ${fields.mobileFontSize};` : ""}
                    `,
                    null,
                    null,
                ])}
        `,
    };
    return (
        <Typography as="h1" data-test-selector="PageTitle" {...titleStyle}>
            {pageTitle || ""}
        </Typography>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(PageTitle),
    definition: {
        group: "Basic",
        icon: "heading",
        fieldDefinitions: [
            {
                name: fields.marginAllSide,
                displayName: "Margin",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.horizontalTextAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: " left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
            },
            {
                name: fields.textTransform,
                editorTemplate: "DropDownField",
                defaultValue: "none",
                fieldType: "General",
                options: [
                    { displayName: "Capitalize", value: "capitalize" },
                    { displayName: "Uppercase", value: "uppercase" },
                    { displayName: "Lowercase", value: "lowercase" },
                    { displayName: "None", value: "none" },
                ],
            },
            {
                name: fields.mobileMarginAllSide,
                displayName: "Mobile Margin",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override mobile margin (e.g. '16px 20px'). Leave empty to use desktop margin.",
                sortOrder: 10,
            },
            {
                name: fields.mobileFontSize,
                displayName: "Mobile Font Size",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override mobile font size (e.g. '24px'). Leave empty for default.",
                sortOrder: 11,
            },
            
        ],
    },
};

export default widgetModule;